import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';
import { useTenantAuth } from '../context/TenantAuthContext';

const STATUSES = ['active', 'inactive', 'invited'];

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'invited') return 'warning';
  return 'default';
}

export default function TenantUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useTenantAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/users/${userId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setUser(json.user);
    } catch (e) {
      setError(e.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const patchStatus = async (status) => {
    setBusy(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/users/${userId}/status`, {
        method: 'PATCH',
        body: { status }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Update failed');
      setUser(json.user);
      setConfirmStatus(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !user) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!user) return null;

  const isSelf = String(me?.id) === String(user.id);
  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{user.full_name || user.email}</Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/tenant/users')}>
            Back to list
          </Button>
          <TenantCan permission="tenant.users.update">
            <Button variant="contained" onClick={() => navigate(`/tenant/users/${userId}/edit`)}>
              Edit
            </Button>
          </TenantCan>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
          <Chip label={user.status} color={statusColor(user.status)} />
        </Box>

        <TenantCan anyOf={['tenant.users.update', 'tenant.users.disable']}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {STATUSES.map((s) => {
              const disabled =
                busy ||
                user.status === s ||
                (isSelf && s === 'inactive') ||
                (isSelf && s === 'invited');
              return (
                <Button
                  key={s}
                  size="small"
                  disabled={disabled}
                  onClick={() => {
                    if (s === 'inactive' && !isSelf) setConfirmStatus(s);
                    else patchStatus(s);
                  }}
                >
                  Set {s}
                </Button>
              );
            })}
          </Box>
        </TenantCan>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          Profile
        </Typography>
        <Grid container spacing={2}>
          {row('First name', user.first_name)}
          {row('Last name', user.last_name)}
          {row('Full name', user.full_name)}
          {row('Mobile', user.mobile_number)}
          {row('Roles', (user.roles || []).map((r) => r.name).join(', ') || '—')}
          {row('Last login', user.last_login_at ? String(user.last_login_at) : '—')}
          {row('Invited at', user.invited_at ? String(user.invited_at) : '—')}
          {row('Activated at', user.activated_at ? String(user.activated_at) : '—')}
          {row('Created', user.created_at ? String(user.created_at) : '—')}
          {row('Updated', user.updated_at ? String(user.updated_at) : '—')}
        </Grid>
      </Paper>

      <Dialog open={Boolean(confirmStatus)} onClose={() => !busy && setConfirmStatus(null)}>
        <DialogTitle>Deactivate user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This user will not be able to sign in until reactivated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStatus(null)} disabled={busy}>
            Cancel
          </Button>
          <Button color="warning" onClick={() => patchStatus('inactive')} disabled={busy}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
